import React from "react";
import { useLang } from "@/context/LanguageContext";

const COPY = {
  en: {
    title: "Terms & Privacy",
    updated: "Last updated: February 2026",
    terms_heading: "Terms of Use",
    terms: [
      "DIYLegal.info provides general legal-literacy, educational, organizational, and resource-navigation information. It does not provide legal advice, legal strategies, legal defenses, legal opinions, or legal representation.",
      "DIYLegal.info is not a law firm. Use of the platform does not create an attorney-client relationship.",
      "AI-generated summaries, explanations, and case-law overviews may be incomplete or incorrect. Always verify with official court sources or a licensed attorney before relying on any content.",
      "The Community Court Navigator (Nina Watts) is a resource guide, not an attorney, and does not provide legal advice.",
      "If you need legal advice or representation, contact a licensed attorney or legal-aid organization.",
    ],
    privacy_heading: "Privacy",
    privacy: [
      "We collect only what is necessary to provide the service: account email (if you sign in), your case checklist and timeline, learning progress, and files you choose to upload.",
      "Uploaded files are stored in encrypted object storage. Signed-in users' files are owner-only; anonymous uploads are one-shot for the current session.",
      "Do NOT upload Social Security numbers, passwords, financial-account credentials, or confidential attorney-client communications.",
      "You may delete your saved data at any time from My Case, the Waiver builder, or your account settings.",
      "AI providers (OpenAI, Anthropic) process message text to generate responses. Do not include sensitive personal information in questions.",
    ],
    contact_heading: "Contact",
    contact: "Questions about the platform? Text our Community Court Navigator, Nina Watts, via the Navigator page.",
  },
  es: {
    title: "Términos y Privacidad",
    updated: "Última actualización: febrero de 2026",
    terms_heading: "Términos de Uso",
    terms: [
      "DIYLegal.info ofrece información educativa, organizativa y de navegación de recursos. No brinda asesoría legal, estrategias legales, defensas legales, opiniones legales ni representación legal.",
      "DIYLegal.info no es un bufete de abogados. Usar la plataforma no crea una relación abogado-cliente.",
      "Los resúmenes, explicaciones y descripciones de casos generados por IA pueden ser incompletos o incorrectos. Verifica siempre con fuentes oficiales de la corte o con un abogado con licencia antes de basarte en cualquier contenido.",
      "La Navegadora de la Corte Comunitaria (Nina Watts) es una guía de recursos, no una abogada, y no brinda asesoría legal.",
      "Si necesitas asesoría o representación legal, comunícate con un abogado con licencia o una organización de ayuda legal.",
    ],
    privacy_heading: "Privacidad",
    privacy: [
      "Solo recopilamos lo necesario para brindar el servicio: correo electrónico (si inicias sesión), tu lista de preparación y línea de tiempo, tu progreso de aprendizaje y los archivos que decidas subir.",
      "Los archivos subidos se almacenan en almacenamiento cifrado. Los archivos de usuarios registrados son privados; los archivos anónimos son de uso único para la sesión actual.",
      "NO subas números de Seguro Social, contraseñas, credenciales de cuentas financieras ni comunicaciones confidenciales entre abogado y cliente.",
      "Puedes eliminar tus datos guardados en cualquier momento desde Mi Caso, el creador de exenciones o los ajustes de tu cuenta.",
      "Los proveedores de IA (OpenAI, Anthropic) procesan el texto de los mensajes para generar respuestas. No incluyas información personal sensible en las preguntas.",
    ],
    contact_heading: "Contacto",
    contact: "¿Preguntas sobre la plataforma? Envíale un mensaje a nuestra Navegadora Nina Watts desde la página de la Navegadora.",
  },
};

export default function Legal() {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.en;
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="legal-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">{t.title}</h1>
        <p className="text-xs text-[#707880] mt-1">{t.updated}</p>
      </div>
      <Section title={t.terms_heading} items={t.terms} testid="legal-terms" />
      <Section title={t.privacy_heading} items={t.privacy} testid="legal-privacy" />
      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5" data-testid="legal-contact">
        <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t.contact_heading}</div>
        <p className="mt-2">{t.contact}</p>
      </div>
    </div>
  );
}

function Section({ title, items, testid }) {
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5" data-testid={testid}>
      <h2 className="text-lg font-bold">{title}</h2>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 leading-relaxed">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#FAF5EC] text-[#C85A32] font-bold text-xs flex items-center justify-center">{i + 1}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
